import { useState } from "react";
import { corelToolsService } from "../services/corelToolsService";
import { OneAgruparToolData } from "../types/tools";
import type { OneAgruparCacheResult } from "./useOneAgruparCache";
import { sendToTerminal } from "../../../lib/terminalLogger";
import { api, APIError } from "../../../lib/api";
import { TokenService } from "../../../lib/tokenService";
import { refreshToken } from "../../../lib/auth";
import {
	fetchWithSsoRetry,
	getBaseBearer,
	ensureFreshSsoToken,
	getShortSsoToken,
} from "../../../lib/toolsSso";
import { API_URL } from "../../../config";
import { VBCompilerService } from "../../../lib/corel/actions/vbcompiler";
import { registerExecution } from "../../../lib/registerExecution";
import { corelCommunication } from "../../../lib/corel/communication";
import { apiWithRetry } from "../../../lib/apiRetry";
import { notifyToolsError } from "../../../lib/toolsErrorNotification";
import { useNotifications } from "../../../components/feedback/NotificationProvider";
import { ensurePlotCalcApiConfig } from "../../../lib/corel/tools";
import { getWithCacheETag, clearAllToolsCache } from "../../../lib/apiCache";
import { getTenantIdForWebSocket } from "../../../utils/getTenantId";

type OneAgruparPrecheckResult = {
	ok: boolean;
	selectedObjectsCount: number;
	limit: number;
	requiresConfirmation: boolean;
};

type LoadedOneAgruparTool = {
	tool: OneAgruparToolData;
	precheck: OneAgruparPrecheckResult;
};

const TEST_QPDF_INSTALL_DIRECTORY = "C:\\Program Files\\qpdf 12.3.2";

interface UseToolExecutionProps {
	setSendingId: (id: string | null) => void;
	setOneAgruparRunning: (running: boolean) => void;
	setShowCollectMetadata: (show: boolean) => void;
	setCollectMetadataResult: (result: any) => void;
	setErrorMessage: (message: string | null) => void;
	oneAgruparToolData: OneAgruparToolData | null;
	oneAgruparCache?: OneAgruparCacheResult; // Hook do OneAgrupar para carregamento sob demanda
}

export function useToolExecution({
	setSendingId,
	setOneAgruparRunning,
	setShowCollectMetadata,
	setCollectMetadataResult,
	setErrorMessage,
	oneAgruparToolData,
	oneAgruparCache,
}: UseToolExecutionProps) {
	const { notify } = useNotifications();
	const [isLayersUnlocked, setIsLayersUnlocked] = useState(false);

	const executeRunPs = async () => {
		try {
			setSendingId("runps");
			const resp = await corelToolsService.runPhotoshopExternalEdit();
			const status = (resp as any)?.status || (resp as any)?.Status;
			if (status === "error") {
				setErrorMessage("Erro ao enviar para Photoshop.");
			} else if (status === "timeout") {
				setErrorMessage(
					"Tempo limite ao aguardar o Photoshop. Tente novamente.",
				);
			}
		} finally {
			setSendingId(null);
		}
	};

	const executeCollectMetadata = async () => {
		try {
			setSendingId("collectmetadata");
			const resp = await corelToolsService.collectMetadata();
			setCollectMetadataResult(resp);
			setShowCollectMetadata(true);
		} finally {
			setSendingId(null);
		}
	};

	const executeSmartImport = async () => {
		try {
			setSendingId("smartimport");
			await corelToolsService.smartImport();
		} finally {
			setSendingId(null);
		}
	};

	const executeOpenFreeFile = async () => {
		try {
			setSendingId("open-free-file");
			await corelToolsService.openFreeFile();
		} finally {
			setSendingId(null);
		}
	};

	const getErrorMessageByCode = (
		errorCode: string | null,
		fallbackError?: string,
	): string => {
		switch (errorCode) {
			case "network":
				return "Sem conexão com o servidor. Verifique sua internet e tente novamente.";
			case "auth":
				return "Sessão expirada. Faça login novamente.";
			case "validation":
				return "Dados da ferramenta inválidos. Tente recarregar a página.";
			default:
				return fallbackError || "Ocorreu um erro inesperado. Tente novamente.";
		}
	};

	const runOneAgruparPrecheck = async (): Promise<OneAgruparPrecheckResult> => {
		return await new Promise((resolve, reject) => {
			try {
				const requestId = corelCommunication.sendActionGuarded(
					"oneagrupar:precheck",
					{},
					(response: any) => {
						if (response?.status !== "ok") {
							reject(
								new Error(
									response?.message || "Falha ao validar seleção do OneAgrupar.",
								),
							);
							return;
						}

						const result = response?.result;
						if (!result || typeof result !== "object") {
							reject(
								new Error("Resposta inválida no precheck do OneAgrupar."),
							);
							return;
						}

						resolve({
							ok: Boolean(result.ok),
							selectedObjectsCount: Number(result.selectedObjectsCount || 0),
							limit: Number(result.limit || 0),
							requiresConfirmation: Boolean(result.requiresConfirmation),
						});
					},
				);

				if (!requestId) {
					reject(
						new Error(
							"Falha ao enviar precheck do OneAgrupar para o Corel.",
						),
					);
					return;
				}

				sendToTerminal("📤 [ONEAGRUPAR:PRECHECK] Enviado", { requestId });
			} catch (error) {
				reject(error instanceof Error ? error : new Error(String(error)));
			}
		});
	};

	const prepareOneAgruparExecution = async (): Promise<LoadedOneAgruparTool> => {
		try {
			setOneAgruparRunning(true);
			setErrorMessage(null);

			// ✅ CORRIGIDO: Seguindo padrão do OneBitmapV1Page
			// Fazer fetch direto e aguardar resposta completa antes de executar
			// Isso elimina a race condition do cache com lazy loading

			try {
				// ✅ ATUALIZADO: Usar cache inteligente com ETag
				// - Se código não mudou no servidor, usa cache local (304 Not Modified)
				// - Se código mudou, baixa versão nova automaticamente
				const endpoint = `/tools/agrupar-objetos-sobrepostos`;

				sendToTerminal("🔍 [ONEAGRUPAR] Carregando ferramenta...", {
					endpoint,
					useETag: true,
				});

				const { data, fromCache, etag } = await getWithCacheETag<any>({
					key: "tool:agrupar-objetos-sobrepostos",
					endpoint,
					ttlMs: 30 * 60 * 1000, // 30 minutos
					area: "local",
				});

				sendToTerminal(
					fromCache
						? "🟢 [ONEAGRUPAR] Cache HIT (304)"
						: "🔵 [ONEAGRUPAR] Cache MISS - dados novos",
					{ etag },
				);

				// Validar dados recebidos
				if (!data || typeof data !== "object" || !data.code || !data.defaults) {
					throw new Error("Dados da ferramenta inválidos");
				}

				// Validar estrutura dos defaults
				if (
					typeof data.defaults.marginTolerancia !== "number" ||
					typeof data.defaults.prefixoNomeGrupo !== "string" ||
					typeof data.defaults.usarNomenclaturaNumerica !== "boolean"
				) {
					throw new Error("Configurações padrão da ferramenta inválidas");
				}

				sendToTerminal("✅ [ONEAGRUPAR] Ferramenta carregada, executando...", {
					hasCode: !!data.code,
					hasConfig: !!data.config,
					slug: data.slug,
				});

				const precheck = await runOneAgruparPrecheck();
				sendToTerminal("🔍 [ONEAGRUPAR:PRECHECK] Resultado", precheck);

				if (!precheck.ok) {
					throw new Error("Precheck do OneAgrupar retornou resposta inválida.");
				}

				// ✅ Dados já estão disponíveis aqui - sem race condition!
				const td: OneAgruparToolData = {
					slug: data.slug || "agrupar-objetos-sobrepostos",
					version: data.version,
					code: data.code,
					defaults: {
						marginTolerancia: data.defaults.marginTolerancia,
						prefixoNomeGrupo: data.defaults.prefixoNomeGrupo,
						usarNomenclaturaNumerica: data.defaults.usarNomenclaturaNumerica,
					},
					config: data.config,
				};

				// Validar se o código existe e não está vazio
				const code = td.code;
				if (!code || typeof code !== "string" || code.trim().length === 0) {
					throw new Error("Código da ferramenta não encontrado");
				}

				return {
					tool: td,
					precheck,
				};
			} catch (err) {
				const errorMsg = err instanceof Error ? err.message : String(err);
				sendToTerminal("❌ [ONEAGRUPAR] Erro ao carregar/executar ferramenta", {
					error: errorMsg,
				});
				setErrorMessage(`Erro: ${errorMsg}`);
				throw err;
			}
		} finally {
			setOneAgruparRunning(false);
		}
	};

	const executePreparedOneAgrupar = async (tool: OneAgruparToolData) => {
		try {
			setOneAgruparRunning(true);
			setErrorMessage(null);

			const resp = await corelToolsService.oneAgruparExecute(tool);
			if ((resp as any)?.status === "error") {
				setErrorMessage(
					"Erro ao executar: " + (resp?.message || "Tente novamente."),
				);
			}
			return resp;
		} finally {
			setOneAgruparRunning(false);
		}
	};

	const executeOneAgruparDirect = async () => {
		await prepareOneAgruparExecution();
	};

	const executeCommand = async (id: number) => {
		try {
			setSendingId(`command:${id}`);

			// ✅ Pipeline para MontaTec (command:1) e Mult+MontaTec (command:2)
			// Garante que plotcalc:set-api-config seja enviado antes de executar
			// Isso evita falhas no VBA quando a config não foi atualizada na Registry
			if (id === 1 || id === 2) {
				sendToTerminal(
					`🔄 [COMMAND:${id}] Garantindo configuração PlotCalc antes de executar...`,
					{},
				);

				const configResult = await ensurePlotCalcApiConfig({ maxRetries: 3 });

				if (!configResult.success) {
					sendToTerminal(
						`❌ [COMMAND:${id}] Falha ao configurar PlotCalc após ${configResult.attempts} tentativas`,
						{
							error: configResult.error,
							attempts: configResult.attempts,
						},
					);
					setErrorMessage(
						"Não foi possível configurar o PlotCalc. Feche a aba de ferramentas e tente abrir novamente.",
					);
					return;
				}

				sendToTerminal(`✅ [COMMAND:${id}] PlotCalc configurado com sucesso`, {
					attempts: configResult.attempts,
				});
			}

			const resp = await corelToolsService.runCommandById(id);
			// Não exibir modal para timeouts do MontaTec; apenas log já foi emitido.
			if (
				(resp as any)?.status === "error" &&
				resp?.message &&
				!String(resp.message).toLowerCase().includes("tempo limite")
			) {
				setErrorMessage(
					"Erro ao executar comando: " + (resp?.message || "Erro desconhecido"),
				);
			}
		} finally {
			setSendingId(null);
		}
	};

	const executeExportBatchLocal = async () => {
		try {
			setSendingId("export_batch_local");
			const resp = await corelToolsService.exportBatchLocal();
			if ((resp as any)?.status === "error") {
				setErrorMessage(
					"Erro ao exportar em lote: " + (resp?.message || "Tente novamente."),
				);
			}
		} finally {
			setSendingId(null);
		}
	};

	const executeRepairPdf = async () => {
		try {
			setSendingId("repair-pdf");

			notify({
				type: "info",
				title: "Reparar PDF",
				message: "Abrindo fluxo nativo de reparo...",
				duration: 2500,
			});

			const qpdfDirectory =
				localStorage.getItem("qpdfDirectory") ||
				localStorage.getItem("launcher:qpdfDirectory") ||
				localStorage.getItem("one1tech:qpdfDirectory") ||
				TEST_QPDF_INSTALL_DIRECTORY;

			sendToTerminal("🔍 [REPAIR_PDF] Diretório qpdf resolvido no frontend", {
				qpdfDirectory,
				source:
					localStorage.getItem("qpdfDirectory")
						? "localStorage:qpdfDirectory"
						: localStorage.getItem("launcher:qpdfDirectory")
							? "localStorage:launcher:qpdfDirectory"
							: localStorage.getItem("one1tech:qpdfDirectory")
								? "localStorage:one1tech:qpdfDirectory"
								: "test-fallback:qpdf-install-directory",
			});

			const response = await corelToolsService.repairPdf(qpdfDirectory);
			const status = response?.status;
			const result = response?.result;
			const message =
				response?.message ||
				response?.error ||
				"Não foi possível concluir o reparo do PDF.";

			if (status === "ok") {
				sendToTerminal("✅ [REPAIR_PDF] Fluxo concluído com sucesso", {
					result,
				});

				notify({
					type: "success",
					title: "PDF Reparado",
					message:
						response?.message || "O PDF foi reparado e aberto com sucesso.",
					duration: 5000,
				});
				return;
			}

			if (status === "cancelled" || response?.code === "pdf_selection_cancelled") {
				sendToTerminal("⚠️ [REPAIR_PDF] Operação cancelada pelo usuário");
				return;
			}

			sendToTerminal("❌ [REPAIR_PDF] Falha no fluxo", {
				response,
			});

			notify({
				type: "error",
				title: "Erro ao Reparar PDF",
				message,
				duration: 6000,
			});
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Erro desconhecido";

			sendToTerminal("❌ [REPAIR_PDF] Exceção ao executar", {
				error: errorMessage,
			});

			notify({
				type: "error",
				title: "Erro ao Reparar PDF",
				message: errorMessage,
				duration: 6000,
			});
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa ferramenta que faz GET para obter código VB e executa diretamente
	 * Padrão: GET /tools/{toolSlug} -> executa VB sem progress
	 */
	const executeToolFromApi = async (toolSlug: string, toolId: string) => {
		try {
			setSendingId(toolId);

			// ✅ ATUALIZADO: Usar cache inteligente com ETag
			// - Se código não mudou no servidor, usa cache local (304 Not Modified)
			// - Se código mudou, baixa versão nova automaticamente
			const endpoint = `/tools/${toolSlug}`;

			sendToTerminal(`🔍 [${toolId.toUpperCase()}] Carregando ferramenta...`, {
				endpoint,
				useETag: true,
			});

			const {
				data: toolData,
				fromCache,
				etag,
			} = await getWithCacheETag<any>({
				key: `tool:${toolSlug}`,
				endpoint,
				ttlMs: 30 * 60 * 1000, // 30 minutos
				area: "local",
			});

			sendToTerminal(
				fromCache
					? `🟢 [${toolId.toUpperCase()}] Cache HIT (304)`
					: `🔵 [${toolId.toUpperCase()}] Cache MISS - dados novos`,
				{ etag },
			);

			// Validar dados recebidos
			if (!toolData || typeof toolData !== "object" || !toolData.code) {
				throw new Error("Dados da ferramenta inválidos");
			}

			sendToTerminal(
				`✅ [${toolId.toUpperCase()}] Ferramenta carregada, executando...`,
				{
					hasCode: !!toolData.code,
					hasConfig: !!toolData.config,
					slug: toolData.slug,
				},
			);

			// Preparar payload para execução
			const vbid = toolData.config?.vbid || String(toolData.slug || toolSlug);
			const code = String(toolData.code || "").trim();

			if (!code) {
				throw new Error("Código da ferramenta não encontrado");
			}

			// ✅ CORRIGIDO: Respeitar 100% o que vem da API (seguindo padrão das outras ferramentas)
			const result = await VBCompilerService.executeWithCode(
				vbid,
				code,
				{}, // Sem parâmetros
				{
					// Respeitar configurações da API com fallbacks seguros
					progress: toolData.config?.progress ?? false,
					progressEvery: toolData.config?.progressEvery ?? 1,
					progressIntervalMs: toolData.config?.progressIntervalMs ?? 100,
					progressLabel: toolData.config?.progressLabel ?? "Processamento",
					progressStartEnd: toolData.config?.progressStartEnd ?? true,
					uiModal: toolData.config?.uiModal ?? false,
					uiCancelable: toolData.config?.uiCancelable ?? false,
					disablePerformanceMonitor:
						toolData.config?.disablePerformanceMonitor ?? false,
				},
			);

			// Registrar execução na API
			if (result.requestId) {
				try {
					registerExecution(
						{
							requestId: result.requestId,
							status: result.success ? "ok" : "error",
							message: result.error,
							result: result.response?.result,
						},
						toolData,
						{},
					).catch((err) => {
						sendToTerminal(
							`⚠️ [${toolId.toUpperCase()}] Erro ao registrar execução`,
							{
								error: String(err),
							},
						);
					});
				} catch { }
			}

			if (!result.success) {
				const errorMsg = result.error || "Erro ao executar ferramenta";
				setErrorMessage(`Erro: ${errorMsg}`);
				sendToTerminal(`❌ [${toolId.toUpperCase()}] Erro na execução`, {
					error: errorMsg,
					status: result.status,
				});
			} else {
				sendToTerminal(
					`✅ [${toolId.toUpperCase()}] Execução concluída com sucesso`,
					{
						requestId: result.requestId,
					},
				);
			}
		} catch (err: any) {
			const errorMsg = err instanceof Error ? err.message : String(err);
			setErrorMessage(`Erro ao executar: ${errorMsg}`);
			sendToTerminal(`❌ [${toolId.toUpperCase()}] Erro`, {
				error: errorMsg,
			});
		} finally {
			setSendingId(null);
		}
	};

	const executeMultiplicadorObjetos = async () => {
		await executeToolFromApi("multiplicador-objetos", "multiplicador-objetos");
	};

	const executeGridObjetos = async () => {
		await executeToolFromApi("grid-objetos", "grid-objetos");
	};

	/**
	 * Executa criar-circulo para Contração DTF Têxtil
	 * Config: diametro: 0.75mm, cor: Cian 100%
	 */
	const executeContracaoDtfTextil = async () => {
		try {
			setSendingId("contracao-dtf-textil");

			sendToTerminal(
				"🔍 [CONTRAÇÃO DTF TÊXTIL] Criando círculo de contração...",
				{
					diametro: 0.75,
					cor: "Cian 100%",
				},
			);

			const requestId = corelCommunication.sendActionGuarded(
				"criar-circulo",
				{
					diametro: 0.75,
					corCmyk: {
						c: 100,
						m: 0,
						y: 0,
						k: 0,
					},
					usarPosicaoMouse: true,
					semContorno: true,
				},
				(response) => {
					if (response.status === "ok") {
						sendToTerminal(
							"✅ [CONTRAÇÃO DTF TÊXTIL] Círculo criado com sucesso",
							{
								result: response.result,
							},
						);
					} else {
						sendToTerminal("❌ [CONTRAÇÃO DTF TÊXTIL] Erro ao criar círculo", {
							error: response.message,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error("Falha ao enviar comando criar-circulo");
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [CONTRAÇÃO DTF TÊXTIL] Exceção ao executar", {
				error: errorMsg,
			});
			throw error;
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa criar-circulo para Contração DTF UV
	 * Config: diametro: 0.5mm, corSpot: Roland VersaWorks/CutContour
	 */
	const executeContracaoDtfUv = async () => {
		try {
			setSendingId("contracao-dtf-uv");

			sendToTerminal("🔍 [CONTRAÇÃO DTF UV] Criando círculo de contração...", {
				diametro: 0.5,
				corSpot: "Roland VersaWorks / CutContour",
			});

			const requestId = corelCommunication.sendActionGuarded(
				"criar-circulo",
				{
					diametro: 0.5,
					corSpot: {
						palette: "Roland VersaWorks",
						name: "CutContour",
						tint: 100,
					},
					usarPosicaoMouse: true,
					semContorno: true,
				},
				(response) => {
					if (response.status === "ok") {
						sendToTerminal("✅ [CONTRAÇÃO DTF UV] Círculo criado com sucesso", {
							result: response.result,
						});
					} else {
						sendToTerminal("❌ [CONTRAÇÃO DTF UV] Erro ao criar círculo", {
							error: response.message,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error("Falha ao enviar comando criar-circulo");
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [CONTRAÇÃO DTF UV] Exceção ao executar", {
				error: errorMsg,
			});
			throw error;
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa girar-objeto para rotacionar objetos selecionados em 45°
	 * Config: angulo: 45.0
	 */
	const executeGirarObjeto = async () => {
		try {
			setSendingId("girar-objeto");

			sendToTerminal("🔍 [GIRAR OBJETO] Rotacionando objeto(s) em 45°...", {
				angulo: 45.0,
			});

			const requestId = corelCommunication.sendActionGuarded(
				"girar-objeto",
				{
					angulo: 45.0,
				},
				(response) => {
					if (response.status === "ok") {
						sendToTerminal(
							"✅ [GIRAR OBJETO] Objeto(s) rotacionado(s) com sucesso",
							{
								result: response.result,
							},
						);
					} else {
						sendToTerminal("❌ [GIRAR OBJETO] Erro ao rotacionar objeto(s)", {
							error: response.message || response.error,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error("Falha ao enviar comando girar-objeto");
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [GIRAR OBJETO] Exceção ao executar", {
				error: errorMsg,
			});
			throw error;
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Aplica de fato o revert (modo "confirmar") para um snapshot específico.
	 * Chamado pelo botão "Reverter agora" da notificação de confirmação.
	 */
	const confirmarRevertPipeline = (snapshotId: string) => {
		const reqId = corelCommunication.sendActionGuarded(
			"reverter-pipeline",
			{ modo: "confirmar", snapshotId },
			(response) => {
				if (response.status === "ok") {
					const r: any = response.result || {};
					sendToTerminal("✅ [REVERTER PIPELINE] Revert aplicado", { result: r });
					const base = `${r.objetosRevertidos ?? 0} objeto(s) restaurado(s) e ${r.paginasRemovidas ?? 0} página(s) removida(s).`;
					if (r.verificado === false) {
						// Verificação de pós-condição encontrou divergências: alertar em vez de sucesso.
						const divs: string[] = r.divergencias || [];
						notify({
							type: "warning",
							title: "Revert aplicado — com divergências",
							message: `${base} A verificação encontrou ${divs.length} divergência(s): ${divs.slice(0, 3).join(" | ")}${divs.length > 3 ? "…" : ""}`,
							sticky: true,
						});
					} else {
						notify({
							type: "success",
							title: "Organização revertida",
							message: `${base} Estado final verificado ✓`,
						});
					}
				} else {
					sendToTerminal("❌ [REVERTER PIPELINE] Falha ao aplicar revert", {
						error: response.message || response.error,
					});
					notify({
						type: "error",
						title: "Reverter Organização",
						message: response.message || "Falha ao aplicar o revert.",
					});
				}
			},
		);
		if (!reqId) {
			notify({
				type: "error",
				title: "Reverter Organização",
				message: "Falha ao enviar a confirmação do revert.",
			});
		}
	};

	/**
	 * Reverte a organização/distribuição (pipeline) para o estado anterior, usando o snapshot
	 * lógico capturado antes dela. Fluxo em duas etapas: primeiro um "preview" (não muta nada)
	 * que relata o que será feito; o usuário confirma numa notificação e só então aplica.
	 * @see spec/index_snapshot_revert_pipeline_organizacao_20260710_1500.md (backend)
	 */
	const executeReverterPipeline = async () => {
		try {
			setSendingId("reverter-pipeline");
			sendToTerminal("🔍 [REVERTER PIPELINE] Calculando preview do revert...");

			const requestId = corelCommunication.sendActionGuarded(
				"reverter-pipeline",
				{ modo: "preview" },
				(response) => {
					if (response.status !== "ok") {
						notify({
							type: "warning",
							title: "Reverter Organização",
							message: response.message || "Não há nada para reverter neste documento.",
						});
						return;
					}

					const p: any = response.result || {};
					if (!p.snapshotId) {
						notify({
							type: "warning",
							title: "Reverter Organização",
							message: "Nenhum snapshot disponível para reverter.",
						});
						return;
					}

					const revertiveis: number = p.objetosRevertiveis ?? 0;
					const naoEncontrados: string[] = p.objetosNaoEncontrados || [];
					const paginas: string[] = p.paginasSeraoRemovidas || [];

					const partes = [
						`${revertiveis} objeto(s) voltarão ao layout original.`,
						paginas.length ? `${paginas.length} página(s) criada(s) serão removidas.` : null,
						naoEncontrados.length
							? `⚠️ ${naoEncontrados.length} objeto(s) do snapshot não foram encontrados (edição manual?) e serão ignorados.`
							: null,
					].filter(Boolean);

					notify({
						type: naoEncontrados.length ? "warning" : "info",
						title: "Reverter Organização?",
						message: partes.join(" "),
						sticky: true,
						actions: [
							{
								label: "Reverter agora",
								onClick: () => confirmarRevertPipeline(p.snapshotId),
							},
						],
					});
				},
			);

			if (!requestId) {
				throw new Error("Falha ao enviar preview de reverter-pipeline");
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [REVERTER PIPELINE] Exceção ao executar", {
				error: errorMsg,
			});
			throw error;
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa Layout.Grid para organizar objetos em grid
	 * Config padrão: 5 colunas x 4 linhas, espaçamento 50mm, espaçamento entre grids 150mm
	 * @see docs/frontend/layout-grid.md
	 */
	const executeLayoutGrid = async () => {
		try {
			setSendingId("layout-grid");

			sendToTerminal("📐 [LAYOUT GRID] Organizando objetos em grid...", {
				columns: 5,
				rows: 4,
				spacingMm: 50,
				gridSpacingMm: 150,
			});

			const requestId = corelCommunication.sendActionGuarded(
				"corelworks:tool",
				{
					toolId: "Layout.Grid",
					dryRun: false,
					options: {
						columns: 5,
						rows: 4,
						spacingMm: 50,
						gridSpacingMm: 150,
						sortBySize: true,
						sortDescending: true,
					},
				},
				(response) => {
					if (response.status === "ok") {
						sendToTerminal("✅ [LAYOUT GRID] Grid criado com sucesso", {
							result: response.result,
						});
					} else {
						sendToTerminal("❌ [LAYOUT GRID] Erro ao criar grid", {
							error: response.message,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error(
					"Falha ao enviar comando corelworks:tool (Layout.Grid)",
				);
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [LAYOUT GRID] Exceção ao executar", {
				error: errorMsg,
			});
			throw error;
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa criar-retangulo para criar Folha A3 UV
	 * Config: largura: 400mm, altura: 285mm, corContorno: "#EC2A90"
	 */
	const executeCriarRetangulo = async () => {
		try {
			setSendingId("criar-retangulo");

			sendToTerminal("📐 [FOLHA A3 UV] Criando retângulo 400mm x 285mm...", {
				largura: 400.0,
				altura: 285.0,
				corContorno: "#EC2A90",
			});

			const requestId = corelCommunication.sendActionGuarded(
				"criar-retangulo",
				{
					largura: 400.0,
					altura: 285.0,
					corContorno: "#EC2A90",
				},
				(response) => {
					if (response.status === "ok") {
						sendToTerminal("✅ [FOLHA A3 UV] Retângulo criado com sucesso", {
							result: response.result,
						});
					} else {
						sendToTerminal("❌ [FOLHA A3 UV] Erro ao criar retângulo", {
							error: response.message || response.error,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error("Falha ao enviar comando criar-retangulo");
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [FOLHA A3 UV] Exceção ao executar", {
				error: errorMsg,
			});
			throw error;
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa refresh para atualizar a tela do CorelDRAW
	 * Força redesenho da interface após processos pesados
	 * Inclui feedback visual via toast notifications
	 */
	const executeRefresh = async () => {
		try {
			setSendingId("refresh");

			sendToTerminal("🔄 [REFRESH] Atualizando interface do CorelDRAW...");

			// Mostrar toast de progresso
			notify({
				type: "info",
				title: "Atualizando",
				message: "Atualizando interface do CorelDRAW...",
				duration: 2000,
			});

			// Enviar action "refresh" para o backend C#
			const requestId = corelCommunication.sendActionGuarded(
				"refresh",
				{},
				(response) => {
					if (response.status === "ok") {
						sendToTerminal("✅ [REFRESH] Interface atualizada com sucesso", {
							result: response.result,
						});

						notify({
							type: "success",
							title: "Tela Atualizada!",
							message: "A interface do CorelDRAW foi atualizada com sucesso.",
							duration: 3000,
						});
					} else {
						const errorMsg =
							response.message || response.error || "Erro desconhecido";
						sendToTerminal("❌ [REFRESH] Erro ao atualizar interface", {
							error: errorMsg,
						});

						notify({
							type: "error",
							title: "Erro ao atualizar",
							message: errorMsg,
							duration: 5000,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error("Falha ao enviar comando refresh");
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [REFRESH] Exceção ao executar", {
				error: errorMsg,
			});

			notify({
				type: "error",
				title: "Erro ao atualizar",
				message: errorMsg,
				duration: 5000,
			});
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa multiplicar-objeto para Multiplicar v2.0
	 * Abre modal no CorelDRAW para usuário digitar quantidade
	 * Pré-requisito: 1 objeto selecionado
	 */
	const executeMultiplicarObjetoV2 = async () => {
		try {
			setSendingId("multiplicar-objeto-v2");

			sendToTerminal(
				"🔢 [MULTIPLICAR V2] Abrindo modal de multiplicação...",
				{},
			);

			// Mostrar toast de progresso
			notify({
				type: "info",
				title: "Multiplicar Objeto",
				message: "Abrindo modal para definir quantidade...",
				duration: 2000,
			});

			const requestId = corelCommunication.sendActionGuarded(
				"multiplicar-objeto",
				{},
				(response) => {
					if (response.status === "ok") {
						const result = response.result || {};
						sendToTerminal(
							"✅ [MULTIPLICAR V2] Objetos multiplicados com sucesso",
							{
								result,
							},
						);

						notify({
							type: "success",
							title: "Multiplicação Concluída!",
							message:
								result.message ||
								`${result.copiasCriadas || 0} cópia(s) criada(s) com sucesso.`,
							duration: 4000,
						});
					} else if (response.status === "cancelled") {
						sendToTerminal(
							"⚠️ [MULTIPLICAR V2] Operação cancelada pelo usuário",
							{},
						);

						notify({
							type: "info",
							title: "Operação Cancelada",
							message: "A multiplicação foi cancelada.",
							duration: 3000,
						});
					} else {
						const errorMsg =
							response.message || response.error || "Erro desconhecido";
						sendToTerminal("❌ [MULTIPLICAR V2] Erro ao multiplicar objeto", {
							error: errorMsg,
						});

						notify({
							type: "error",
							title: "Erro ao Multiplicar",
							message: errorMsg,
							duration: 5000,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error("Falha ao enviar comando multiplicar-objeto");
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [MULTIPLICAR V2] Exceção ao executar", {
				error: errorMsg,
			});

			notify({
				type: "error",
				title: "Erro ao Multiplicar",
				message: errorMsg,
				duration: 5000,
			});
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa limpar-cache para limpar cache de ferramentas
	 * Limpa tanto localStorage quanto sessionStorage
	 * Inclui feedback visual e delay mínimo para UX
	 */
	const executeLimparCache = async () => {
		try {
			setSendingId("limpar-cache");

			sendToTerminal("🗑️ [LIMPAR CACHE] Limpando cache de ferramentas...");

			// Mostrar toast de progresso
			notify({
				type: "info",
				title: "Aguarde",
				message: "Limpando cache de ferramentas...",
				duration: 1500,
			});

			// Pequeno delay para feedback visual (mínimo 500ms para o usuário perceber a ação)
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Limpar cache de ambas as áreas
			const sessionCount = clearAllToolsCache("session");
			const localCount = clearAllToolsCache("local");
			const totalCount = sessionCount + localCount;

			sendToTerminal("✅ [LIMPAR CACHE] Cache limpo com sucesso", {
				sessionKeys: sessionCount,
				localKeys: localCount,
				total: totalCount,
			});

			// Notificar usuário com resultado
			if (totalCount > 0) {
				const details =
					sessionCount > 0 && localCount > 0
						? ` (Sessão: ${sessionCount}, Local: ${localCount})`
						: "";
				notify({
					type: "success",
					title: "Cache Limpo!",
					message: `${totalCount} item(s) removido(s) com sucesso${details}`,
					duration: 4000,
				});
			} else {
				notify({
					type: "info",
					title: "Cache vazio",
					message: "Nenhum item de cache encontrado para remover.",
					duration: 3000,
				});
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [LIMPAR CACHE] Exceção ao executar", {
				error: errorMsg,
			});

			// Notificar erro ao usuário
			notify({
				type: "error",
				title: "Erro ao limpar cache",
				message: errorMsg,
				duration: 5000,
			});
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Alterna o estado das camadas (Habilitar/Desabilitar)
	 * Não requer permissão
	 */
	const executeGerenciarCamadas = async (
		scope: "active" | "document" = "document",
	) => {
		try {
			setSendingId("gerenciar-camadas");

			const newState = !isLayersUnlocked;
			const actionLabel = newState ? "Habilitando" : "Desabilitando";

			sendToTerminal(
				`🔒 [GERENCIAR CAMADAS] ${actionLabel} camadas... (Escopo: ${scope})`,
			);

			// notify({
			// 	type: "info",
			// 	title: "Gerenciar Camadas",
			// 	message: `${actionLabel} edição de camadas...`,
			// 	duration: 2000,
			// });

			const payload = {
				action: "gerenciar-camadas",
				data: {
					target: "all",
					names: [],
					visible: newState,
					editable: newState,
					pageScope: scope,
				},
			};

			const requestId = corelCommunication.sendActionGuarded(
				payload.action,
				payload.data,
				(response: any) => {
					if (response.status === "ok") {
						setIsLayersUnlocked(newState);

						const result = response.result || {};
						const modifiedCount = result.modifiedCount ?? 0;
						const processedItems = result.processedItems || [];

						// Log detalhado para debug (não mostrar tudo ao usuário)
						sendToTerminal(`✅ [GERENCIAR CAMADAS] Sucesso`, {
							modifiedCount,
							processedItemsCount: processedItems.length,
							items: processedItems, // Logar itens para debug
							scope: result.scope,
							mode: result.mode,
						});

						// Mensagem amigável para o usuário
						const actionText = newState ? "habilitados" : "desabilitados";
						const countText =
							modifiedCount > 0
								? `${modifiedCount} itens foram ${actionText}.`
								: `Nenhum item precisou ser alterado (já estavam ${actionText}).`;

						const baseMsg = newState
							? "Camadas HABILITADAS para edição."
							: "Camadas DESABILITADAS (Bloqueadas).";

						notify({
							type: "success",
							title: newState ? "Camadas Desbloqueadas" : "Camadas Bloqueadas",
							message: `${baseMsg} ${countText}`,
							duration: 4000,
						});
					} else {
						// Tratamento de erro (ex: nenhum documento)
						const errorMsg =
							response.message || "Erro desconhecido ao gerenciar camadas.";
						sendToTerminal("❌ [GERENCIAR CAMADAS] Erro", {
							error: errorMsg,
							originalResponse: response,
						});

						notify({
							type: "error",
							title: "Erro",
							message: errorMsg,
							duration: 5000,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error(
					"Falha ao enviar comando gerenciar-camadas (WebView indisponível)",
				);
			}
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [GERENCIAR CAMADAS] Exceção", { error: errorMsg });
			notify({
				type: "error",
				title: "Erro",
				message: errorMsg,
				duration: 5000,
			});
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa fechar-documento (Close Document)
	 * A confirmação é feita via modal (CloseDocumentConfirmModal)
	 * Payload fixo: { acao: "fechar-documento", requestId: "REQ-12345", dados: {} }
	 */
	const executeCloseDocument = async () => {
		sendToTerminal("🖱️ [CLOSE_DOCUMENT] Execução iniciada", {});

		try {
			setSendingId("close-document");
			sendToTerminal("🔒 [CLOSE_DOC] Iniciando fechamento de documento...");

			// Mostrar toast de progresso
			notify({
				type: "info",
				title: "Fechando...",
				message: "Enviando comando para fechar documento...",
				duration: 2000,
			});

			// 2. Executar Serviço
			const resp = await corelToolsService.closeDocument();

			// 3. Tratar Resposta
			// Exemplo Sucesso: { requestId: "...", status: "ok", result: { sucesso: true, mensagem: "...", arquivoFechado: "..." } }
			const result = resp?.result || {};

			if (result.sucesso) {
				const arquivo = result.arquivoFechado
					? ` (${result.arquivoFechado})`
					: "";
				const msg = result.mensagem || "Documento fechado com sucesso.";

				sendToTerminal("✅ [CLOSE_DOC] Sucesso", {
					arquivo: result.arquivoFechado,
				});

				notify({
					type: "success",
					title: "Sucesso",
					message: msg + arquivo,
					duration: 4000,
				});
			} else {
				// Falha de negócio (ex: nenhum documento aberto)
				const msg = result.mensagem || "Erro ao fechar documento.";

				sendToTerminal("⚠️ [CLOSE_DOC] Falha de negócio", { mensagem: msg });

				notify({
					type: "warning", // Warning pois foi executado mas não teve efeito esperado
					title: "Atenção",
					message: msg,
					duration: 4000,
				});
			}
		} catch (err: any) {
			// Erro Técnico (timeout, falha de rede, etc)
			const errorMsg = err instanceof Error ? err.message : String(err);
			sendToTerminal("❌ [CLOSE_DOC] Erro técnico", { error: errorMsg });

			notify({
				type: "error",
				title: "Erro",
				message: `Falha na execução: ${errorMsg}`,
				duration: 5000,
			});
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * Executa Auto Bitmap
	 * Ferramenta direta (sem UI)
	 * Envia request corelworks:tool com id = Bitmap.Convert.Pipeline
	 */
	const executeAutoBitmap = async () => {
		try {
			setSendingId("auto-bitmap");

			sendToTerminal("🖼️ [AUTO BITMAP] Solicitando conversão...");

			// Mostrar feedback visual imediato
			notify({
				type: "info",
				title: "Auto Bitmap",
				message: "Iniciando conversão...",
				duration: 2000,
			});

			const requestId = corelCommunication.sendActionGuarded(
				"corelworks:tool",
				{
					toolId: "Bitmap.Convert.Pipeline",
					dryRun: false,
					options: {
						autoMode: true,
						scope: "Selection",
						targetColorMode: "CMYK",
						useAutoDpiTable: true,
						manualDpi: 300,
						materialId: "DTF_TEXTIL",
						antiAliasing: false,
						transparent: true,
						overprintBlack: false,
						flattenContainers: false,
						gcInterval: 20,
					},
				},
				(response) => {
					if (response.status === "ok") {
						const result = response.result?.metrics || {};
						const converted = result.convertedCount || 0;
						const failed = result.failedCount || 0;
						const duration = result.durationMs
							? (result.durationMs / 1000).toFixed(1) + "s"
							: "";

						sendToTerminal("✅ [AUTO BITMAP] Conversão concluída", {
							result: response.result,
						});

						if (failed > 0) {
							notify({
								type: "warning",
								title: "Conversão Concluída com Alertas",
								message: `${converted} shapes convertidos. ${failed} falhas. Tempo: ${duration}`,
								duration: 5000,
							});
						} else {
							notify({
								type: "success",
								title: "Conversão Concluída!",
								message: `${converted} shapes convertidos com sucesso. Tempo: ${duration}`,
								duration: 4000,
							});
						}
					} else if (response.status === "cancelled") {
						// Usuário cancelou no dialog WPF
						sendToTerminal("⚠️ [AUTO BITMAP] Cancelado pelo usuário");
						// Não mostrar erro, foi intencional
					} else if (response.status === "progress_update") {
						// Atualização de progresso intermediária — pipeline ainda em andamento
						return;
					} else {
						const errorMsg =
							response.message ||
							(response as any).error ||
							"Erro desconhecido";

						// Tratar erros específicos conhecidos
						if (errorMsg.includes("Nenhum shape")) {
							notify({
								type: "warning",
								title: "Seleção Necessária",
								message: "Selecione objetos no CorelDRAW e tente novamente.",
								duration: 4000,
							});
							return;
						}

						if (errorMsg.includes("Nenhum documento")) {
							notify({
								type: "warning",
								title: "Documento Fechado",
								message: "Abra um arquivo .cdr no CorelDRAW primeiro.",
								duration: 4000,
							});
							return;
						}

						sendToTerminal("❌ [AUTO BITMAP] Erro na conversão", {
							error: errorMsg,
						});

						notify({
							type: "error",
							title: "Erro na Conversão",
							message: errorMsg,
							duration: 5000,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error(
					"Falha ao enviar comando corelworks:tool (Bitmap.Convert.Pipeline)",
				);
			}
		} catch (err: any) {
			const errorMsg = err instanceof Error ? err.message : String(err);
			sendToTerminal("❌ [AUTO BITMAP] Erro técnico", { error: errorMsg });

			notify({
				type: "error",
				title: "Erro",
				message: `Falha na execução: ${errorMsg}`,
				duration: 5000,
			});
		} finally {
			setSendingId(null);
		}
	};

	/**
	 * MontaTech 2.0 Nativo — Abre modal WPF no C# para nesting
	 * Fluxo:
	 * 1. ensureFreshSsoToken → garante token SSO atualizado
	 * 2. ensurePlotCalcApiConfig → grava token/apiUrl na Registry do C#
	 * 3. GET /plotcalc/config → busca profiles do backend
	 * 4. sendActionGuarded("render-2d-viewer", { profiles, accessToken }) → C# abre modal WPF
	 * 5. C# faz coleta + POST direto no backend para calcular nesting
	 *
	 * SEGURANÇA DE TOKEN:
	 * - Token é enviado TANTO via Registry (ensurePlotCalcApiConfig) QUANTO no payload
	 * - C# usa payload como fonte primária, Registry como fallback
	 * - Isso garante que o token mais fresco possível chegue ao C#
	 */
	const executeMontaTechNative = async () => {
		try {
			setSendingId("montatech-native");

			// ── Step 1: Forçar refresh do token SSO ──
			sendToTerminal(
				"🔑 [MONTATECH-NATIVE] Garantindo token SSO atualizado...",
			);

			try {
				await ensureFreshSsoToken();
				sendToTerminal("✅ [MONTATECH-NATIVE] Token SSO atualizado");
			} catch (ssoErr) {
				sendToTerminal(
					"⚠️ [MONTATECH-NATIVE] Falha ao atualizar SSO, usando token existente",
					{
						error: ssoErr instanceof Error ? ssoErr.message : String(ssoErr),
					},
				);
				// Não bloqueia — usa o token que tem (accessToken como fallback)
			}

			// ── Step 2: Gravar config atualizada na Registry do C# ──
			sendToTerminal(
				"🔄 [MONTATECH-NATIVE] Gravando config PlotCalc na Registry...",
			);

			const configResult = await ensurePlotCalcApiConfig({ maxRetries: 3 });

			if (!configResult.success) {
				sendToTerminal("❌ [MONTATECH-NATIVE] Falha ao configurar PlotCalc", {
					error: configResult.error,
					attempts: configResult.attempts,
				});
				setErrorMessage(
					"Não foi possível configurar o PlotCalc. Feche a aba de ferramentas e tente novamente.",
				);
				return;
			}

			sendToTerminal("✅ [MONTATECH-NATIVE] PlotCalc configurado", {
				attempts: configResult.attempts,
			});

			// ── Step 3: Buscar config COMPLETO do backend (serial + profiles) ──
			sendToTerminal(
				"📥 [MONTATECH-NATIVE] Buscando config completo do backend...",
			);

			const tenantId = 1
			const configEndpoint = `/plotcalc/config-full?tenantId=${tenantId}`;
			const configUrl = `${API_URL}${configEndpoint}`;

			const configRes = await fetchWithSsoRetry(
				configUrl,
				{ method: "GET", headers: { "Content-Type": "application/json" } },
				getBaseBearer(),
			);

			if (!configRes.ok) {
				throw new Error(`Erro ao buscar config: HTTP ${configRes.status}`);
			}

			const configData = await configRes.json();
			const rawProfiles =
				configData?.data?.profiles || configData?.profiles || [];
			// profiles pode vir como array ou como objeto { services: [...] }
			const profiles = Array.isArray(rawProfiles)
				? rawProfiles
				: rawProfiles?.services || [];
			const serial = configData?.data?.serial || configData?.serial || null;

			sendToTerminal("📋 [MONTATECH-NATIVE] Config carregado", {
				profilesCount: profiles.length,
				hasSerial: !!serial,
				profileNames: profiles.map((p: any) => p.name).slice(0, 5),
			});

			if (!profiles.length) {
				setErrorMessage(
					"Nenhum perfil encontrado. Configure perfis no backend.",
				);
				return;
			}

			if (!serial) {
				sendToTerminal(
					"⚠️ [MONTATECH-NATIVE] Nenhum serial encontrado - TCP direto não funcionará",
				);
			}

			// ── Step 3.5: Aplicar serial + profiles na Registry C# (para TCP direto) ──
			if (serial && profiles.length > 0) {
				sendToTerminal(
					"💾 [MONTATECH-NATIVE] Salvando serial + profiles na Registry...",
				);

				try {
					const applyConfigResult = await new Promise<any>(
						(resolve, reject) => {
							const requestId = corelCommunication.sendActionGuarded(
								"plotcalc:apply-config",
								{
									serial,
									profiles,
								},
								(response) => {
									if (response?.status === "ok") {
										resolve(response);
									} else {
										reject(
											new Error(response?.message || "Falha ao aplicar config"),
										);
									}
								},
							);

							if (!requestId) {
								reject(new Error("Falha ao enviar plotcalc:apply-config"));
							}

							// Timeout de 5s
							setTimeout(
								() => reject(new Error("Timeout ao aguardar resposta")),
								5000,
							);
						},
					);

					sendToTerminal(
						"✅ [MONTATECH-NATIVE] Serial + profiles salvos na Registry",
						{
							result: applyConfigResult?.result,
						},
					);
				} catch (applyErr) {
					sendToTerminal(
						"⚠️ [MONTATECH-NATIVE] Falha ao salvar na Registry (TCP direto não funcionará)",
						{
							error:
								applyErr instanceof Error ? applyErr.message : String(applyErr),
						},
					);
					// Não bloqueia - HTTP ainda funciona
				}
			}

			// ── Step 4: Enviar action render-2d-viewer para C# ──
			// Token enviado no payload como fonte primária (mais fresco que a Registry)
			const freshToken = getShortSsoToken() || TokenService.getAccessToken();
			const multiTenantId = getTenantIdForWebSocket();

			sendToTerminal(
				"📤 [MONTATECH-NATIVE] Enviando render-2d-viewer para C#...",
				{
					profileCount: profiles.length,
					hasToken: !!freshToken,
					tokenLength: freshToken?.length,
				},
			);

			notify({
				type: "info",
				title: "MontaTech 2.0",
				message: `Abrindo com ${profiles.length} perfil(s)...`,
				duration: 2000,
			});

			const requestId = corelCommunication.sendActionGuarded(
				"render-2d-viewer",
				{
					profiles,
					accessToken: freshToken,
					apiUrl: API_URL,
					multiTenantId,
				},
				(response) => {
					if (response.status === "ok") {
						sendToTerminal("✅ [MONTATECH-NATIVE] Modal C# concluído", {
							result: response.result,
						});
						notify({
							type: "success",
							title: "MontaTech 2.0",
							message: "Operação concluída com sucesso.",
							duration: 3000,
						});
					} else if (response.status === "cancelled") {
						sendToTerminal(
							"⚠️ [MONTATECH-NATIVE] Operação cancelada pelo usuário",
						);
					} else {
						const errorMsg =
							response.message || response.error || "Erro desconhecido";
						sendToTerminal("❌ [MONTATECH-NATIVE] Erro no modal C#", {
							error: errorMsg,
						});
						notify({
							type: "error",
							title: "Erro MontaTech 2.0",
							message: errorMsg,
							duration: 5000,
						});
					}
				},
			);

			if (!requestId) {
				throw new Error("Falha ao enviar action render-2d-viewer");
			}

			// ── Token Keep-Alive: Refresh periódico enquanto modal C# está aberto ──
			// O modal pode ficar aberto por minutos. Tokens SSO expiram em ~5 min.
			// Refresh a cada 4 min garante Registry sempre com token fresco.
			sendToTerminal(
				"🔄 [MONTATECH-NATIVE] Iniciando token keep-alive (refresh a cada 4 min)",
			);

			const keepAliveInterval = setInterval(
				async () => {
					try {
						sendToTerminal(
							"🔄 [MONTATECH-NATIVE] Token keep-alive: refreshing...",
						);
						await ensureFreshSsoToken();
						await ensurePlotCalcApiConfig({
							maxRetries: 1,
							timeoutPerRetry: 3000,
						});
						sendToTerminal(
							"✅ [MONTATECH-NATIVE] Token keep-alive: Registry atualizada",
						);
					} catch (keepAliveErr) {
						// Falha silenciosa - não interrompe a experiência do usuário
						sendToTerminal(
							"⚠️ [MONTATECH-NATIVE] Token keep-alive falhou (não crítico)",
							{
								error:
									keepAliveErr instanceof Error
										? keepAliveErr.message
										: String(keepAliveErr),
							},
						);
					}
				},
				4 * 60 * 1000,
			); // 4 minutos

			// Auto-limpeza após 30 minutos (tempo máximo razoável para modal aberto)
			setTimeout(
				() => {
					clearInterval(keepAliveInterval);
					sendToTerminal(
						"🛑 [MONTATECH-NATIVE] Token keep-alive encerrado (30 min timeout)",
					);
				},
				30 * 60 * 1000,
			); // 30 minutos
		} catch (error) {
			const errorMsg =
				error instanceof Error ? error.message : "Erro desconhecido";
			sendToTerminal("❌ [MONTATECH-NATIVE] Exceção ao executar", {
				error: errorMsg,
			});
			setErrorMessage(errorMsg);

			notify({
				type: "error",
				title: "Erro MontaTech 2.0",
				message: errorMsg,
				duration: 5000,
			});
		} finally {
			setSendingId(null);
		}
	};

	return {
		executeRunPs,
		executeCollectMetadata,
		executeSmartImport,
		executeOpenFreeFile,
		executeOneAgruparDirect,
		prepareOneAgruparExecution,
		executePreparedOneAgrupar,
		executeCommand,
		executeExportBatchLocal,
		executeRepairPdf,
		executeMultiplicadorObjetos,
		executeMultiplicarObjetoV2,
		executeGridObjetos,
		executeContracaoDtfTextil,
		executeContracaoDtfUv,
		executeGirarObjeto,
		executeReverterPipeline,
		executeLayoutGrid,
		executeCriarRetangulo,
		executeRefresh,
		executeLimparCache,
		executeGerenciarCamadas,
		executeCloseDocument,
		executeAutoBitmap,
		executeMontaTechNative,
		isLayersUnlocked,
	};
}
